import type { Ref, SubmitEvent } from "react";

import { useTranslation } from "react-i18next";

import { useGetProfile, useUpdateEvent } from "@/api/bale";
import { CardSubtitleHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";

import { useCurrentEventSuspense } from "../../../../hooks/useCurrentEvent";
import { DiscoverabilitySection } from "./DiscoverabilitySection";
import { InvitesSection } from "./InvitesSection";
import { SharesSection } from "./SharesSection";

export function ManageInvitesModal({ ref, onClose }: { ref: Ref<HTMLDialogElement>; onClose?: () => void }) {
  const { t } = useTranslation();
  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const updateEvent = useUpdateEvent();
  const participantMe = me.isSuccess
    ? event.data.event_participants.find((participant) => participant.profile.id === me.data.id)
    : undefined;

  function updateAttendees(e: SubmitEvent<HTMLFormElement>) {
    const form = new FormData(e.currentTarget);
    const maxAttendees = form.get("max_attendees")! as string;
    const value = maxAttendees.trim() ? Number.parseInt(maxAttendees.trim(), 10) : null;
    updateEvent.mutate({
      eventId: event.data.event.id,
      data: {
        max_attendees: value,
        // TODO: implement patch endpoints? This is crazy and prone to causing error:
        name: event.data.event.name,
        description: event.data.event.description.raw,
        location: event.data.event.location ?? null,
        start_time: event.data.event.start_time ?? null,
        end_time: event.data.event.end_time ?? null,
      },
    });
  }

  const currentAttendees = event.data.event_participants.filter(
    (participant) => participant.response === "Attending",
  ).length;

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="h-200 w-xl">
      <div className="flex flex-col">
        <CardSubtitleHeader>{t("event-manage-sharing")}</CardSubtitleHeader>
        <div className="flex flex-col gap-4 pb-4">
          {me.isSuccess && event.data.event.account_id === me.data.id && (
            <>
              <Form error={updateEvent.error} className="flex flex-col gap-4 px-4" onSubmit={updateAttendees}>
                <p className="font-disinterest">{t("max-attendees-tutorial", { current: currentAttendees })}</p>
                <div className="flex flex-row items-end gap-2">
                  <FormField title={t("event-max-attendees")} className="grow">
                    <Input
                      type="number"
                      name="max_attendees"
                      min={2}
                      defaultValue={event.data.event.max_attendees ?? ""}
                      placeholder={t("event-max-attendees-placeholder")}
                      onChange={() => updateEvent.reset()}
                    />
                  </FormField>
                  <IconButton disabled={updateEvent.isSuccess} name="save" type="submit" />
                </div>
              </Form>
              <hr className="border-divider" />
            </>
          )}
          <DiscoverabilitySection className="px-4" />
          {participantMe &&
            (participantMe.limiting_discoverable_distance === "unlimited" ||
              participantMe.limiting_discoverable_distance > 0) && (
              <>
                <hr className="border-divider" />
                <SharesSection />
                <hr className="border-divider" />
                <InvitesSection />
              </>
            )}
        </div>
      </div>
    </Dialog>
  );
}
